import { Box, Flex, Grid, Stack, Text } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute, Link, notFound } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import Maximize from '~icons/lucide/maximize-2';
import { useMemo, useState } from 'react';
import * as z from 'zod';

import { GameLogo } from '#modules/finder/features/posts/components/SessionParts';
import { locales } from '#features/i18n/constants/locales';
import { RoomSurface } from '#features/session-room/components/RoomSurface';
import { GameLiveHaloPreview } from '#features/session-room/previews/GameLiveHaloPreview';
import {
  Button,
  Chip,
  CommentIcon,
  DeadlineIcon,
  Icon,
  PageHeading,
  Panel,
  PhoneIcon,
} from '#features/ui';
import { IconButton } from '#features/ui/components/IconButton';
import { TierCounts } from '#features/voice/components/SessionVoiceTier/TierCounts';

export const Route = createFileRoute('/{-$locale}/_layout/preview/session-live')({
  ssr: false,
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: zodValidator(z.object({ previewLocale: z.enum(['ru', 'en']).optional() })),
  loaderDeps: ({ search }) => ({ previewLocale: search.previewLocale }),
  loader: async ({ params, deps }) => {
    const locale = deps.previewLocale ?? (params.locale === 'ru' ? 'ru' : 'en');
    const { messages } =
      locale === 'ru'
        ? await import('#features/i18n/locales/ru/messages.po')
        : await import('#features/i18n/locales/en/messages.po');
    return { locale, messages };
  },
  component: SessionLivePreviewRoot,
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
});

function SessionLivePreviewRoot() {
  const { locale, messages } = Route.useLoaderData();
  const previewI18n = useMemo(
    () => setupI18n({ locale, messages: { [locale]: messages } }),
    [locale, messages]
  );
  return (
    <I18nProvider i18n={previewI18n}>
      <SessionLivePreview key={locale} />
    </I18nProvider>
  );
}

type Variant = 'current' | 'label' | 'arc' | 'edge' | 'pulse';
type Phase = 'live' | 'upcoming' | 'over';
const VARIANTS: Variant[] = ['current', 'label', 'arc', 'edge', 'pulse'];
const COLORS = ['fg.warning', 'fg.error', 'purple.300'] as const;
const breathe = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: .6; }
`;

/** Visual fixtures only: no room store, requests, microphone or session mutations. */
function WidgetSample({
  variant,
  phase,
  color,
  motion,
  large,
}: {
  variant: Variant;
  phase: Phase;
  color: string;
  motion: boolean;
  large: boolean;
}) {
  const { t } = useLingui();
  const live = phase === 'live';
  const status = live
    ? t`Session is on`
    : phase === 'upcoming'
      ? t`Starts in 09:59`
      : t`Session ended`;
  return (
    <RoomSurface
      data-testid={`live-sample-${variant}`}
      position="relative"
      zIndex="base"
      w="100%"
      maxW="300px"
      direction="row"
      align="center"
      px={2}
      py={1}
      gap={2}
      flexWrap="wrap"
      role="group"
      aria-label={status}
      css={!motion ? { '& *': { animation: 'none !important' } } : undefined}
      _before={
        variant === 'edge' && live
          ? {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              w: '0.5',
              h: '4',
              bg: 'status.live.solid',
              borderRadius: 'full',
            }
          : undefined
      }
    >
      <Flex align="center" gap={2} flex={1} minW="max-content" title={status}>
        <Flex
          position="relative"
          align="center"
          justify="center"
          boxSize={variant === 'arc' ? 8 : 5}
          flexShrink={0}
          _after={
            variant === 'arc' && live
              ? {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  borderRadius: 'full',
                  borderWidth: '2px',
                  borderColor: 'status.live.solid',
                  borderTopColor: 'transparent',
                  borderRightColor: 'transparent',
                  transform: 'rotate(-45deg)',
                }
              : undefined
          }
        >
          <GameLogo
            icon="/preview/session-chats/apex-mark.svg"
            color={color}
            diameter={20}
            status={variant === 'current' ? phase : phase === 'over' ? 'over' : undefined}
            ringInset={0}
            ringMotion="orbit"
            announceStatus={false}
          />
        </Flex>
        {live && (variant === 'label' || variant === 'pulse') ? (
          <Flex align="center" gap={1} flexShrink={0}>
            <Box
              boxSize={1.5}
              borderRadius="full"
              bg="status.live.solid"
              aria-hidden="true"
              css={
                variant === 'pulse' && motion
                  ? {
                      animation: `${breathe} calc(var(--chakra-durations-ambient) * .6) var(--chakra-easings-ease-in-out) infinite`,
                      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
                    }
                  : undefined
              }
            />
            <Text textStyle="caption" color="fg.secondary">
              LIVE
            </Text>
          </Flex>
        ) : null}
        {phase === 'upcoming' ? (
          <Flex align="center" gap={1} color="fg.muted" flexShrink={0}>
            <Icon boxSize={3}>
              <DeadlineIcon />
            </Icon>
            <Text textStyle="caption" fontVariantNumeric="tabular-nums">
              09:59
            </Text>
          </Flex>
        ) : phase === 'over' ? (
          <Text textStyle="caption" color="fg.muted" whiteSpace="nowrap">{t`Session ended`}</Text>
        ) : null}
        <TierCounts
          accepted={large ? 12 : 2}
          capacity={large ? 16 : 3}
          count={{
            kind: 'voice',
            connected: large ? 10 : 1,
            capacity: large ? 16 : 3,
            live: false,
          }}
          compact
          inline
          showVoice={phase !== 'over'}
        />
      </Flex>
      <Flex align="center" gap={2} ml="auto" flexShrink={0}>
        <IconButton
          variant="ghost"
          aria-label={t`Open chat`}
          disabled
          boxSize={{ base: 11, md: 10 }}
          minW={{ base: 11, md: 10 }}
          color="fg.icon"
          _disabled={{ opacity: 1 }}
        >
          <CommentIcon />
        </IconButton>
        {phase !== 'over' ? (
          <IconButton
            variant="solid"
            colorPalette="purple"
            aria-label={t`post:session.room.joinVoice`}
            disabled
            boxSize={{ base: 11, md: 10 }}
            minW={{ base: 11, md: 10 }}
            _disabled={{ opacity: 1 }}
          >
            <PhoneIcon />
          </IconButton>
        ) : null}
        <IconButton
          variant="ghost"
          bg="bg.muted"
          aria-label={t`post:session.room.expand`}
          disabled
          boxSize={{ base: 11, md: 10 }}
          minW={{ base: 11, md: 10 }}
          _disabled={{ opacity: 1 }}
        >
          <Icon boxSize={4.5}>
            <Maximize />
          </Icon>
        </IconButton>
      </Flex>
    </RoomSurface>
  );
}

function SessionLivePreview() {
  const { t } = useLingui();
  const { locale } = Route.useLoaderData();
  const [phase, setPhase] = useState<Phase>('live');
  const [color, setColor] = useState<string>(COLORS[0]);
  const [motion, setMotion] = useState(true);
  const [large, setLarge] = useState(false);
  const [view, setView] = useState<'halos' | 'status'>('halos');
  const titles: Record<Variant, string> = {
    current: t`Current: rotating ring`,
    label: t`1. Static LIVE label`,
    arc: t`2. Static arc with a gap`,
    edge: t`3. Marker at the edge`,
    pulse: t`4. Slow breathing dot`,
  };
  const descriptions: Record<Variant, string> = {
    current: t`One turn every 2 seconds. The ring touches the icon.`,
    label: t`Only the dot is green. The icon keeps its own color; nothing moves.`,
    arc: t`The arc stays still, with space between it and the icon.`,
    edge: t`The status belongs to the whole widget. The icon has no outline.`,
    pulse: t`The dot gently changes brightness every 6 seconds. Its size stays the same.`,
  };
  const phases: { value: Phase; label: string }[] = [
    { value: 'live', label: 'LIVE' },
    { value: 'upcoming', label: t`Upcoming` },
    { value: 'over', label: t`Ended` },
  ];
  const colorLabels = [t`Orange`, t`Red`, t`Purple`];
  return (
    <Stack
      as="main"
      data-testid="session-live-preview"
      maxW="960px"
      mx="auto"
      px={{ base: 3, md: 6 }}
      py={{ base: 6, md: 10 }}
      gap={8}
    >
      <Flex gap={2} flexWrap="wrap">
        {Object.values(locales).map((language) => (
          <Button
            key={language.code}
            size="sm"
            minH={11}
            variant={locale === language.code ? 'solid' : 'outline'}
            asChild
          >
            <Link to="." search={{ previewLocale: language.code as 'ru' | 'en' }} reloadDocument>
              {language.nativeName}
            </Link>
          </Button>
        ))}
      </Flex>
      <PageHeading
        description={t`Compare live effects around the game logo in the actual mini-widget.`}
      >
        {t`Live session concepts`}
      </PageHeading>
      <Flex gap={2} flexWrap="wrap">
        <Button
          minH={11}
          size="sm"
          variant={view === 'halos' ? 'solid' : 'outline'}
          aria-pressed={view === 'halos'}
          onClick={() => setView('halos')}
        >
          {t`Animated game logo`}
        </Button>
        <Button
          minH={11}
          size="sm"
          variant={view === 'status' ? 'solid' : 'outline'}
          aria-pressed={view === 'status'}
          onClick={() => setView('status')}
        >
          {t`Compact indicators`}
        </Button>
        {view === 'halos' ? (
          <Button
            size="sm"
            minH={11}
            variant="outline"
            aria-pressed={!motion}
            onClick={() => setMotion(!motion)}
          >
            {motion ? t`Pause motion` : t`Resume motion`}
          </Button>
        ) : null}
      </Flex>
      {view === 'halos' ? (
        <GameLiveHaloPreview phase={phase} motion={motion} large={large} />
      ) : null}
      <Panel variant="nested" size="md">
        <Stack gap={4}>
          <Stack gap={2}>
            <Text textStyle="strong">{t`Session state`}</Text>
            <Flex gap={2} flexWrap="wrap">
              {phases.map(({ value, label }) => (
                <Button
                  key={value}
                  size="sm"
                  minH={11}
                  variant={phase === value ? 'solid' : 'outline'}
                  aria-pressed={phase === value}
                  onClick={() => setPhase(value)}
                >
                  {label}
                </Button>
              ))}
            </Flex>
          </Stack>
          {view === 'status' ? (
            <Stack gap={2}>
              <Text textStyle="strong">{t`Icon color`}</Text>
              <Flex gap={2} flexWrap="wrap">
                {COLORS.map((value, index) => (
                  <Button
                    key={value}
                    size="sm"
                    minH={11}
                    variant={color === value ? 'solid' : 'outline'}
                    aria-pressed={color === value}
                    onClick={() => setColor(value)}
                  >
                    {colorLabels[index]}
                  </Button>
                ))}
              </Flex>
            </Stack>
          ) : null}
          <Flex gap={2} flexWrap="wrap">
            {view === 'status' ? (
              <Button
                size="sm"
                minH={11}
                variant="outline"
                aria-pressed={!motion}
                onClick={() => setMotion(!motion)}
              >
                {motion ? t`Pause motion` : t`Resume motion`}
              </Button>
            ) : null}
            <Button
              size="sm"
              minH={11}
              variant="outline"
              aria-pressed={large}
              onClick={() => setLarge(!large)}
            >
              {large ? t`Small squad` : t`Large squad`}
            </Button>
          </Flex>
        </Stack>
      </Panel>
      {view === 'status' ? (
        <Grid templateColumns={{ base: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }} gap={8}>
          {VARIANTS.map((variant) => (
            <Stack
              key={variant}
              gap={4}
              minW={0}
              gridColumn={variant === 'current' ? '1 / -1' : undefined}
            >
              <Flex align="center" gap={2} flexWrap="wrap">
                <Text as="h2" textStyle="section">
                  {titles[variant]}
                </Text>
                {variant === 'label' ? <Chip size="sm">{t`My pick`}</Chip> : null}
              </Flex>
              <WidgetSample
                variant={variant}
                phase={phase}
                color={color}
                motion={motion}
                large={large}
              />
              <Text textStyle="body" color="fg.muted" maxW="400px">
                {descriptions[variant]}
              </Text>
            </Stack>
          ))}
        </Grid>
      ) : null}
      <Text textStyle="caption" color="fg.muted">
        {t`Visual samples with demo data. Chat and voice actions are disabled. Your sessions stay unchanged.`}
      </Text>
    </Stack>
  );
}
